export const IS_FREE_FLOW_ENABLED =
  true || (typeof window !== 'undefined' && window.location.href.includes('free-flow'));

export const IS_SHOWCASE_ENABLED = true;

export const USE_TIMESTAMP_POLLING = false; // Timestamp-based polling enabled

/**
 * Controls how the flow / weekly-overview / historical (realtime feed) screens
 * choose their initial trade sides on load.
 *
 * Each screen fires its first data + count query before the user's persisted
 * `optionFlowFilters` have loaded. When the saved trade side differs from the
 * default (Ask / Above Ask), the first fetch uses the wrong side. Both branches
 * below add a settings-driven seed, so the wrong side no longer sticks — they
 * only differ in whether the first fetch waits for it.
 *
 *   false → eager-then-correct (current default): fetch defaults immediately,
 *           then re-seed + refetch once the saved filters resolve. Fast first
 *           paint, brief flash of default-side data, corrects within a beat.
 *   true  → hydrate-before-fetch: wait for the saved filters to resolve so the
 *           first (and only) fetch already uses the correct sides. No flash, but
 *           first paint is one round-trip later.
 *
 * This is a short-term bridge until the feed moves to websockets, at which point
 * this flag and the dual-path branches in each screen should be deleted.
 *
 * TODO(flow-side-hydration): collapse to one strategy and remove this when the
 * websocket feed lands.
 */
export const HYDRATE_FLOW_SIDES_BEFORE_FETCH = false;

/**
 * Realtime feed (WebSocket) rollout flags (docs/realtime-durable-objects-spec.md
 * §14). Env-driven per surface so a phase can be flipped per environment without
 * a code change — set the var to "true" (or "1") to enable. Each must also be
 * listed in next.config.js `env` so Next inlines it into the client bundle.
 *
 * All default false; REALTIME_URL (src/constants/api.ts) is the global kill
 * switch that overrides every flag. When a flag + REALTIME_URL are both set, the
 * surface uses the websocket feed with runtime fallback to its legacy polling path.
 */
const envFlag = (v: string | undefined): boolean => v === 'true' || v === '1';
const hasDevProViewerOverride =
  process.env.NODE_ENV !== 'production' && process.env.NEXT_PUBLIC_DEV_VIEWER_TIER === 'pro';

/** Local UI walkthrough only. The production-mode guard cannot be overridden by an env value. */
export const RETENTION_CANCELLATION_MOCK_ENABLED =
  process.env.NODE_ENV === 'development' &&
  envFlag(process.env.RETENTION_CANCELLATION_MOCK_ENABLED);

/** Self-service Google verification. Firebase remains the sender.
 * Defaults on for web. Explicit false/0 disables it for rollback.
 * Resend/status recovery works in both states. Changes require a new build/deployment.
 */
export const EMAIL_VERIFICATION_SELF_SERVE_ENABLED =
  process.env.EMAIL_VERIFICATION_SELF_SERVE_ENABLED !== 'false' &&
  process.env.EMAIL_VERIFICATION_SELF_SERVE_ENABLED !== '0';

// The remote realtime worker validates real Firebase tokens and therefore sees a synthetic
// development subscriber as anonymous. Use the same-origin, read-only fallback paths for a pro
// override so the visible tier and the returned data cannot disagree.
export const REALTIME_DARKPOOL_ENABLED =
  !hasDevProViewerOverride && envFlag(process.env.REALTIME_DARKPOOL_ENABLED); // Phase 1
export const REALTIME_PRICES_ENABLED = envFlag(process.env.REALTIME_PRICES_ENABLED); // Phase 2
export const REALTIME_FLOW_ENABLED =
  !hasDevProViewerOverride && envFlag(process.env.REALTIME_FLOW_ENABLED); // Phase 3
export const REALTIME_TICKER_FLOW_ENABLED =
  !hasDevProViewerOverride && envFlag(process.env.REALTIME_TICKER_FLOW_ENABLED); // Phase 4
export const REALTIME_GEX_ENABLED = envFlag(process.env.REALTIME_GEX_ENABLED); // Phase 5

/**
 * GEX history: the date control that opens saved end-of-day closes (`?date=`) and the route behind
 * it (`/api/v1/gex/history/[ticker]`). Off unless GEX_HISTORY_ENABLED is "true" or "1": the page
 * then shows Latest only, as it did before history, ignores a `?date=` link, and the route answers
 * 404. Listed in next.config.js `env`, so a change needs a new build. The CSV download and the
 * after-hours Latest valuation do not depend on it.
 */
export const GEX_HISTORY_ENABLED = envFlag(process.env.GEX_HISTORY_ENABLED);

/**
 * Baseline ("full") feed toggle in the flow command center. The full firehose streams
 * every trade (not just smart/unusual flow) and is paid-only + opt-in (defaults OFF per
 * user) because it's heavier bandwidth than the smart feed. This flag controls whether
 * the command is SHOWN at all.
 *
 * Env-driven like the REALTIME_* flags above: defaults OFF, set the var to "true" (or "1")
 * to surface the command — a kill switch we can flip per environment without a code change
 * while we evaluate whether to keep offering it. Must also be listed in next.config.js `env`
 * so Next inlines it into the client bundle. The server still enforces paid-only regardless.
 *
 * Deploy ordering: ship the worker carrying the relaxed no-ticker `full` guard BEFORE
 * enabling this, or a flipped toggle requests a feed the old worker closes ('full requires
 * tickers'), breaking the socket for that user until the worker catches up.
 */
export const FULL_FEED_TOGGLE_ENABLED = envFlag(process.env.FULL_FEED_TOGGLE_ENABLED);

/**
 * Hidden-tab socket close on WEB (RealtimeClient `HIDDEN_CLOSE_MS`, D24). When on, a tab
 * backgrounded for 60 s has its realtime socket closed and must reconnect — token fetch,
 * handshake, and past the 1,500-row diff cap a full multi-MB snapshot — on return.
 *
 * Defaults OFF: hidden web tabs keep streaming. Native/Capacitor builds keep the close
 * regardless of this flag. Full rationale and the cost accounting live with the decision in
 * docs/realtime-durable-objects-spec.md (D24); do not re-litigate it here.
 *
 * Env-driven like the REALTIME_* flags above, so it is flipped per environment without a code
 * change. Next inlines `env` at BUILD time, so changing it still requires a rebuild + deploy.
 */
export const REALTIME_HIDDEN_CLOSE_ENABLED = envFlag(process.env.REALTIME_HIDDEN_CLOSE_ENABLED);

/**
 * The implied-volatility page (`/implied-volatility` and its per-ticker pages).
 *
 * Off while the rest of the branch ships. This is a real kill switch, not a
 * cosmetic one: hiding only the tab would still leave the routes prerendered for
 * the top 500 tickers, listed in the sitemap and `llms.txt`, and linked from the
 * footer, slideout and marketing nav — which is indistinguishable from having
 * launched it. With this off the pages return 404, every link to them is
 * withheld, and nothing is prerendered.
 *
 * Flipping it back on also means restoring the `llms.txt` entry, which is a
 * static file and cannot read this flag.
 */
export const IMPLIED_VOLATILITY_ENABLED = false;

/**
 * Whether the implied-volatility ticker pages are built at deploy time.
 *
 * Off means nothing is prerendered: the route still answers every ticker, but
 * each page is generated on the first request and then cached by ISR like any
 * other. That is the cheaper default. Building the top 500 means 500 option
 * chains fetched during the deploy, for pages most of which no one will ask for
 * that day, and the pages are identical either way once cached.
 *
 * On means the top 500 researched tickers are generated up front, so their very
 * first visitor gets a warm page instead of waiting on a chain fetch. Worth
 * turning on if the slow first hit shows up in Core Web Vitals, or ahead of a
 * push where those pages are expected to land cold traffic all at once.
 *
 * Either way the route uses `fallback: 'blocking'`, so a ticker that was never
 * prerendered still returns complete HTML rather than an empty shell. That
 * distinction is the whole reason not to use `fallback: true` here: a crawler
 * reaching an unbuilt ticker would otherwise index the shell.
 *
 * Has no effect while {@link IMPLIED_VOLATILITY_ENABLED} is off, which refuses
 * every ticker path outright.
 */
export const IMPLIED_VOLATILITY_PRERENDER_TICKERS = false;

/**
 * The "% Change" overlay on the GEX heatmap — per-cell and per-expiry moves
 * against the first snapshot of today's session.
 *
 * Off: the numbers it was reporting are wrong, so it is withheld rather than
 * shown with a caveat. A wrong number on a positioning grid is worse than no
 * number, because it reads as a signal.
 *
 * Off suppresses the whole feature, not just the badges: the toggle button
 * disappears and the baseline snapshot is never captured or stored. That last
 * part matters — leaving capture running would keep writing session baselines
 * whose semantics are what is under suspicion, so a later fix would inherit a
 * pile of records it cannot trust. It also means the visitor's persisted
 * `gex-heatmap-show-change` preference cannot resurrect it.
 *
 * Flip back on once the change math is trusted; nothing else needs to move.
 */
export const GEX_HEATMAP_CHANGE_ENABLED = false;

/**
 * Calculation engines for the TotalFinance integration (docs/totalfinance-integration.md,
 * docs/runbooks/totalfinance-rollout.md).
 *
 *   'legacy'       → the app's original calculations only. This is the rollback: flip a
 *                    constant below back to 'legacy' and nothing TotalFinance-derived is shown.
 *   'shadow'       → the original calculations are displayed; TotalFinance runs beside them on
 *                    the same input snapshot and the page shows the comparison.
 *   'totalfinance' → TotalFinance results are displayed. The original calculation stays in the
 *                    code as the guard: a TotalFinance failure (and any disagreement beyond the
 *                    guard's tolerances) shows the original result instead.
 *
 * The constants are the code default. NEXT_PUBLIC_TOTALFINANCE_OPC_MODE and
 * NEXT_PUBLIC_TOTALFINANCE_GEX_MODE override them per environment; Next inlines both at BUILD
 * time, so a change needs a rebuild. An unrecognised override value selects 'legacy', so a
 * mistyped rollback still rolls back.
 */
export type CalculationEngineMode = 'legacy' | 'shadow' | 'totalfinance';

/**
 * Options profit calculator: chart, payoff table and time slider valuations. TotalFinance, guarded:
 * legacy still prices every request and is shown for any surface where the two disagree (reported
 * to Sentry as reason codes). Set to 'legacy' to switch TotalFinance off entirely.
 */
export const OPC_CALCULATION_ENGINE: CalculationEngineMode = 'totalfinance';

/**
 * Gamma/delta exposure pages: exposure aggregation, price profile, zero gamma and skew. TotalFinance,
 * guarded like the OPC: legacy still runs on every recompute and the whole dashboard shows legacy
 * whenever the two disagree (reported to Sentry as reason codes). Set to 'legacy' to switch
 * TotalFinance off entirely.
 */
export const GEX_CALCULATION_ENGINE: CalculationEngineMode = 'totalfinance';

/**
 * The legacy guard on the gamma/delta exposure pages, in a 'totalfinance' build.
 *
 *   true  → every full calculation (a chain update, a setting change, at least once a minute; not
 *           a live-spot tick, see GEX_SPOT_RESCALE) also runs legacy on the same contracts and
 *           shows legacy wherever the two disagree. About a third of a full calculation (SPX:
 *           282 vs 192 ms on an M4), nothing per tick.
 *   false → TotalFinance is trusted: legacy runs only when TotalFinance cannot be shown (it throws,
 *           returns a non-finite or non-conserved result, or meets observations it cannot value
 *           honestly), and the page then shows legacy, reported to Sentry exactly as today. A
 *           finite, conserved but wrong TotalFinance result would then be shown.
 *
 * Keep it on until Sentry has shown no GEX disagreements for long enough to trust TotalFinance
 * alone (docs/runbooks/totalfinance-rollout.md). NEXT_PUBLIC_TOTALFINANCE_GEX_GUARD=off (or on)
 * overrides it per environment at build time; any other value keeps the guard on.
 */
export const GEX_LEGACY_GUARD = true;

/**
 * Live-spot ticks on the gamma/delta exposure pages, in every engine mode (utils/client/gex/
 * gexLiveSpot). true: a tick that only moves the spot rescales the last full calculation, which
 * gives the same numbers as calculating again; the chain, a setting or the price-profile grid
 * changing, or a minute passing, still calculates in full. false: every tick calculates in full,
 * as before. NEXT_PUBLIC_GEX_SPOT_RESCALE=off (or on) overrides it per environment at build time;
 * any other value turns it off, so a mistyped override restores the old behaviour.
 */
export const GEX_SPOT_RESCALE = true;

export function getInitialPollingTimestamp(): string {
  const yesterday = new Date();
  yesterday.setHours(yesterday.getHours() - 24);
  return yesterday.toISOString();
}
